import { PlusIcon } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import VariableList from "@/components/InputVariableConfigurator/ConfiguratorContent/VariableList";
import { Variable } from "@/components/InputVariableConfigurator/types";
import { Button } from "@/components/ui/button";
import useAutoScroll from "@/hooks/useAutoScroll";

interface HeadersSectionProps {
  headers: Record<string, unknown>;
  onHeadersChange: (headers: Record<string, unknown>) => void;
  disabled?: boolean;
}

const HeadersSection = ({ headers, onHeadersChange, disabled = false }: HeadersSectionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const isInitializedRef = useRef(false);

  // Keep headers in local state to prevent re-mounting
  const [localHeaders, setLocalHeaders] = useState<[string, string][]>(() => {
    if (headers) {
      return Object.entries(headers).map(
        ([key, value]) =>
          [key, typeof value === "object" ? JSON.stringify(value) : String(value || "")] as [
            string,
            string,
          ],
      );
    }
    return [];
  });

  // Sync local headers with store on initialization only
  useEffect(() => {
    if (!isInitializedRef.current && headers) {
      const headersArray = Object.entries(headers).map(
        ([key, value]) =>
          [key, typeof value === "object" ? JSON.stringify(value) : String(value || "")] as [
            string,
            string,
          ],
      );
      setLocalHeaders(headersArray);
      isInitializedRef.current = true;
    }
  }, [headers]);

  // Create stable variables array with consistent IDs
  const variables = useMemo(
    () =>
      localHeaders.map((header, index) => ({
        key: header[0],
        value: header[1],
        id: `header-${index}`, // Use index for stable IDs
      })),
    [localHeaders],
  );

  const { lastItemRef } = useAutoScroll(variables, ref);

  useLayoutEffect(() => {
    const scrollContainer = ref.current;
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollPositionRef.current;
    }
  });

  const handleScroll = useCallback(() => {
    if (ref.current) {
      scrollPositionRef.current = ref.current.scrollTop;
    }
  }, []);

  // Update store when local headers change, but only if they're different
  useEffect(() => {
    const newHeaders = Object.fromEntries(localHeaders);
    const hasChanged = JSON.stringify(newHeaders) !== JSON.stringify(headers);

    if (hasChanged && isInitializedRef.current) {
      onHeadersChange(newHeaders);
    }
  }, [localHeaders, headers, onHeadersChange]);

  const handleAddHeader = useCallback(() => {
    setLocalHeaders(prev => [...prev, ["", ""]]);
  }, []);

  const handleUpdateVariable = useCallback((variable: Variable, index: number) => {
    const keyValue = String(variable.key || "");
    const stringValue =
      typeof variable.value === "object"
        ? JSON.stringify(variable.value)
        : String(variable.value || "");

    setLocalHeaders(prev => {
      const newHeaders = [...prev];
      if (index < newHeaders.length) {
        newHeaders[index] = [keyValue, stringValue];
      }
      return newHeaders;
    });
  }, []);

  const handleRemoveVariable = useCallback((index: number) => {
    setLocalHeaders(prev => prev.filter((_, i) => i !== index));
  }, []);

  return (
    <div className="flex flex-col gap-y-2">
      <div ref={ref} className="max-h-[170px] overflow-y-auto" onScroll={handleScroll}>
        <VariableList
          scrollContainerRef={ref}
          showAddFileButton={false}
          variables={variables}
          placeholder="<API_KEY>"
          keyPlaceholder="X-API-Key"
          tooltip="Header value"
          keyTooltip="Header name"
          removeTooltip="Remove header"
          readOnly={disabled}
          updateInputVariable={handleUpdateVariable}
          removeInputVariable={handleRemoveVariable}
          lastItemRef={lastItemRef}
        />
      </div>
      <Button variant="outline" className="w-full" onClick={handleAddHeader} disabled={disabled}>
        <PlusIcon className="mr-2 h-5 w-5 text-gray-600" />
        Add Header
      </Button>
    </div>
  );
};

export default HeadersSection;
